import React, { useMemo, useState } from "react";
import * as Sentry from "@sentry/react";

import { frontendSentryRuntime } from "../instrument";

type AuthResponse = {
  ok: boolean;
  runtime: {
    enabled: string;
    environment: string | null;
    release: string | null;
    project_id: string | null;
  };
};

const apiBase = process.env.REACT_APP_API_URL || "";

export default function SentryCanaryPage() {
  const initialSecret = useMemo(() => new URLSearchParams(window.location.search).get("secret") || "", []);
  const [secret, setSecret] = useState(initialSecret);
  const [authState, setAuthState] = useState<AuthResponse | null>(null);
  const [status, setStatus] = useState("Idle");
  const [lastEventId, setLastEventId] = useState<string | null>(null);

  const verifyAccess = async () => {
    setStatus("Verifying access...");
    const response = await fetch(`${apiBase}/api/v1/health/sentry/canary/auth`, {
      method: "POST",
      headers: {
        "X-Sentry-Canary-Secret": secret,
      },
    });

    if (!response.ok) {
      setAuthState(null);
      setStatus(`Auth failed (${response.status})`);
      return;
    }

    const payload = (await response.json()) as AuthResponse;
    setAuthState(payload);
    setStatus("Access granted");
  };

  const sendFrontendCanary = () => {
    const eventId = Sentry.captureMessage("frontend sentry canary", {
      level: "error",
      tags: {
        canary: "true",
        canary_process: "frontend",
      },
      extra: {
        frontendRuntime: frontendSentryRuntime,
        backendRuntime: authState?.runtime || null,
      },
    });
    setLastEventId(eventId ? String(eventId) : "emitted");
    setStatus("Frontend canary sent");
  };

  return (
    <main style={{ padding: "2rem", fontFamily: "monospace", maxWidth: "48rem", margin: "0 auto" }}>
      <h1>Sentry Canary</h1>
      <p>Use this page to verify Sentry runtime wiring and emit a controlled frontend canary.</p>

      <label htmlFor="secret-input">Canary secret</label>
      <input
        id="secret-input"
        value={secret}
        onChange={(event) => setSecret(event.target.value)}
        type="password"
        style={{ display: "block", width: "100%", margin: "0.5rem 0 1rem", padding: "0.5rem" }}
      />

      <button onClick={verifyAccess} style={{ marginRight: "0.75rem" }}>
        Verify access
      </button>
      <button onClick={sendFrontendCanary} disabled={!authState}>
        Send frontend canary
      </button>

      <p data-testid="status">{status}</p>
      {lastEventId ? <p data-testid="event-id">Event ID: {lastEventId}</p> : null}

      <section>
        <h2>Frontend runtime</h2>
        <pre>{JSON.stringify(frontendSentryRuntime, null, 2)}</pre>
      </section>

      <section>
        <h2>Backend runtime</h2>
        <pre>{JSON.stringify(authState?.runtime || null, null, 2)}</pre>
      </section>
    </main>
  );
}
